import React, { useEffect, useMemo, useState } from "react"

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material"
import { useQueryHrStudentsShort } from "api/reactQuery/queries/hr"
import { HrStudentShort } from "api/reactQuery/queries/hr.types"
import { useParams } from "react-router"

import BunchUsers from "./bunchUsers"
import DraggableUser from "./draggableUser"

const ListUsers = () => {
  const params = useParams()
  const [checkedUsers, setCheckedUsers] = useState<HrStudentShort[]>([])
  const [isToggleAll, setIsToggleAll] = useState(false)
  const [isWithoutJobs, setIsWithoutJobs] = useState(false)
  const { data, isLoading } = useQueryHrStudentsShort({
    classId: Number(params.classId),
    filter: undefined,
    sorting: "asc",
    sort_by: "first_name",
  })

  const allUsers = useMemo(() => {
    return data?.data.objects || []
  }, [data])

  const filteredUsers = useMemo(() => {
    if (isWithoutJobs) {
      return allUsers.filter((item) => !item.jobs.length)
    }
    return allUsers
  }, [isWithoutJobs, allUsers])

  const checkedVisibleUsers = useMemo(() => {
    const filteredIds = new Set(filteredUsers.map((user) => user.id))
    return checkedUsers.filter((user) => filteredIds.has(user.id))
  }, [checkedUsers, filteredUsers])

  const selectAllCheckboxState = useMemo(() => {
    if (filteredUsers.length === 0) {
      return { checked: false, indeterminate: false }
    }
    const checkedCount = checkedVisibleUsers.length
    const totalCount = filteredUsers.length

    if (checkedCount === 0) {
      return { checked: false, indeterminate: false }
    } else if (checkedCount === totalCount) {
      return { checked: true, indeterminate: false }
    } else {
      return { checked: false, indeterminate: true }
    }
  }, [checkedVisibleUsers, filteredUsers])

  const getUsers = (user: HrStudentShort) => {
    const isAlreadyUser = checkedUsers.find((checked) => checked.id === user.id)

    if (isAlreadyUser) {
      setCheckedUsers((prevState) =>
        prevState.filter((checked) => checked.id !== isAlreadyUser.id)
      )
      return
    }

    setCheckedUsers((prev) => [...prev, user])
  }

  const selectAllUsers = () => {
    const filteredIds = new Set(filteredUsers.map((user) => user.id))

    if (checkedVisibleUsers.length === filteredUsers.length) {
      setCheckedUsers((prev) => prev.filter((user) => !filteredIds.has(user.id)))
    } else {
      const notCheckedFiltered = filteredUsers.filter(
        (user) => !checkedUsers.find((checked) => checked.id === user.id)
      )
      setCheckedUsers((prev) => [...prev, ...notCheckedFiltered])
    }
  }

  useEffect(() => {
    if (!checkedUsers.length) setIsToggleAll(false)
  }, [checkedUsers])

  return (
    <Stack sx={{ alignItems: "center", mt: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={isWithoutJobs}
            onChange={() => setIsWithoutJobs(!isWithoutJobs)}
          />
        }
        label={
          <Typography fontWeight="500" variant="body2" pl={1}>
            Show students without a job
          </Typography>
        }
      />
      <Stack p={2} width="100%">
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" p={4}>
            <CircularProgress />
            <Typography variant="body2" color="mockup.neutral40" mt={2}>
              Loading students...
            </Typography>
          </Stack>
        ) : (
          <>
            <Stack width="100%">
              {!!checkedVisibleUsers.length && (
                <BunchUsers
                  checkedUsers={checkedVisibleUsers}
                  onClickX={() => {
                    const visibleIds = new Set(
                      checkedVisibleUsers.map((user) => user.id)
                    )
                    setCheckedUsers((prev) =>
                      prev.filter((user) => !visibleIds.has(user.id))
                    )
                  }}
                />
              )}
              <FormControlLabel
                sx={{
                  margin: "8px 0",
                  alignItems: "center",
                  width: "100%",
                }}
                control={
                  <Checkbox
                    indeterminate={selectAllCheckboxState.indeterminate}
                    checked={selectAllCheckboxState.checked}
                    onChange={selectAllUsers}
                    onClick={() => setIsToggleAll(!isToggleAll)}
                  />
                }
                label="Select All"
              />
            </Stack>
            {filteredUsers?.map((user) => (
              <DraggableUser
                user={user}
                key={user.id}
                checkedUsers={checkedVisibleUsers}
                allCheckedUsers={checkedUsers}
                onChange={() => {
                  getUsers(user)
                }}
              />
            ))}
          </>
        )}
      </Stack>
    </Stack>
  )
}
export default ListUsers
